import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, NavLink, useLocation } from 'react-router-dom';
import { changeValueSearch } from '../../store/reducers/search';
import { Card, Table, Button, Form, Row, Col, Dropdown, Modal, Badge } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { projectPagingAction, deleteProjectAction, changeProjectStatusAction } from '../../store/reducers/project';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import { PROJECT_STATUS_CONST, TASK_STATUS_CONST } from '../../store/constants';
import { toast } from 'react-toastify';
import { userManager } from '@app/utils/oidc';
import { checkIsAdmin } from '../../utils/define';
import Select from 'react-select';
import DatePicker from '@app/components/CustomDatePicker';
import moment from 'moment';
import { Get } from '../../utils/httpProvider';
import { JOBGROUP_API, BaseGatewayImage } from '../../utils/apiUrl';

const getStatusStyle = (color) => {
    const map = {
        'secondary': { bg: '#f6f8fa', text: '#57606a', border: '#d0d7de' },
        'info': { bg: '#ddf4ff', text: '#0969da', border: '#54aeff' },
        'warning': { bg: '#fff8c5', text: '#9a6700', border: '#d4a72c' },
        'success': { bg: '#dafbe1', text: '#1a7f37', border: '#2da44e' },
        'danger': { bg: '#ffebe9', text: '#cf222e', border: '#ff8182' }
    };
    return map[color] || map['secondary'];
};

const normalizeArray = (val) => {
    if (!val) return [];
    if (val.$values) return Array.isArray(val.$values) ? val.$values : [];
    return Array.isArray(val) ? val : [];
};

const normalizeProject = (item) => ({
    ...item,
    members: normalizeArray(item.members ?? item.Members)
});

const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const buildFullName = (source) => {
    if (!source) return '';
    const lastName = source.lastName || source.LastName || '';
    const middleName = source.middleName || source.MiddleName || '';
    const firstName = source.firstName || source.FirstName || '';
    return `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ');
};

const AssignedJobList = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { list, total } = useSelector((state) => state.project);
    const { listAccount, itemAccountAuthInfo } = useSelector((state) => state.account);
    const valueSearch = useSelector((state) => state.search.valueSearch);

    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [keyword, setKeyword] = useState('');
    const [status, setStatus] = useState('');
    const [jobGroupId, setJobGroupId] = useState('');
    const [jobGroups, setJobGroups] = useState([]);
    const [filterFrom, setFilterFrom] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().split('T')[0];
    });
    const [filterTo, setFilterTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [detailProject, setDetailProject] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    const accountLookup = useMemo(() => {
        const lookup = new Map();
        if (!listAccount || !Array.isArray(listAccount)) return lookup;

        listAccount.forEach(item => {
            let id = '';
            let username = '';
            let fullName = '';
            let avatarUrl = '';
            let authInfoId = '';
            
            const hasRoles = item.roles || item.Roles;
            if (hasRoles) {
                id = item.id || item.Id;
                username = item.username || item.Username;
                
                const authInfo = item.accountAuthInfo || item.AccountAuthInfo;
                if (authInfo) {
                    fullName = buildFullName(authInfo);
                    authInfoId = authInfo.id || authInfo.Id || '';
                    
                    const thumbnail = authInfo.imageThumbnail || authInfo.ImageThumbnail;
                    const path = authInfo.imagePath || authInfo.ImagePath;
                    avatarUrl = thumbnail || path || '';
                }
            } else {
                const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
                id = item.accountId || item.AccountId || acc?.id || acc?.Id;
                username = acc?.username || acc?.Username;
                authInfoId = item.id || item.Id || '';
                
                fullName = buildFullName(item);
                
                const thumbnail = item.imageThumbnail || item.ImageThumbnail;
                const path = item.imagePath || item.ImagePath;
                avatarUrl = thumbnail || path || '';
            }

            if (!fullName && username) {
                fullName = username;
            }

            const initials = getInitials(fullName);

            const standardized = {
                id: id ? String(id).trim() : '',
                fullName: fullName ? String(fullName).trim() : '',
                username: username ? String(username).trim() : '',
                avatarUrl: avatarUrl ? String(avatarUrl).trim() : '',
                initials
            };

            if (standardized.id) {
                lookup.set(standardized.id.toLowerCase(), standardized);
            }
            if (authInfoId) {
                lookup.set(String(authInfoId).trim().toLowerCase(), standardized);
            }
            if (standardized.username) {
                lookup.set(standardized.username.toLowerCase(), standardized);
            }
        });

        return lookup;
    }, [listAccount]);

    useEffect(() => {
        Get(JOBGROUP_API.GET_ALL, null, null)
            .then((res) => { if (res) setJobGroups(res); })
            .catch((err) => console.error('Lỗi tải nhóm công việc', err));
    }, []);

    useEffect(() => {
        loadData();
    }, [pageIndex, pageSize]);

    useEffect(() => {
        dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        const fetchUser = async () => {
            const user = await userManager.getUser();
            setCurrentUser(user);
        };
        fetchUser();
    }, [dispatch]);

    const replaceHistory = (page, size, searchKeyword) => {
        const urlParams = new URLSearchParams(location.search);
        urlParams.set('page', page);
        urlParams.set('size', size);
        if (searchKeyword) {
            urlParams.set('search', searchKeyword);
        } else {
            urlParams.delete('search');
        }
        history.replace({ search: urlParams.toString() });
    };

    useEffect(() => {
        const paramUrl = new URLSearchParams(location.search);
        if (paramUrl.get('search') != null && paramUrl.get('search') !== keyword) {
            setKeyword(paramUrl.get('search'));
        }
        if (paramUrl.get('page') != null && Number(paramUrl.get('page')) !== pageIndex) {
            setPageIndex(Number(paramUrl.get('page')));
        }
        if (paramUrl.get('size') != null && Number(paramUrl.get('size')) !== pageSize) {
            setPageSize(Number(paramUrl.get('size')));
        }
    }, []);

    useEffect(() => {
        if (valueSearch !== null && valueSearch !== keyword) {
            setKeyword(valueSearch);
            setPageIndex(1);
            
            const params = { pageIndex: 1, pageSize, keyword: valueSearch, projectType: 1 };
            if (status !== '') params.status = status;
            if (jobGroupId !== '') params.jobGroupId = jobGroupId;
            if (filterFrom !== '') params.fromDate = filterFrom;
            if (filterTo !== '') params.toDate = filterTo;
            
            dispatch(projectPagingAction(params));
            replaceHistory(1, pageSize, valueSearch);
        }
    }, [valueSearch]);

    const isAdmin = useMemo(() => {
        if (!currentUser?.profile?.role) return false;
        const roles = Array.isArray(currentUser.profile.role) 
            ? currentUser.profile.role 
            : [currentUser.profile.role];
        return checkIsAdmin(roles);
    }, [currentUser]);

    const currentUsername = useMemo(() => {
        return currentUser?.profile?.name || currentUser?.profile?.preferred_username;
    }, [currentUser]);

    const loadData = () => {
        const params = { pageIndex, pageSize, keyword, projectType: 1 };
        if (status !== '') params.status = status;
        if (jobGroupId !== '') params.jobGroupId = jobGroupId;
        if (filterFrom !== '') params.fromDate = filterFrom;
        if (filterTo !== '') params.toDate = filterTo;
        dispatch(projectPagingAction(params));
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setPageIndex(1);
        const params = { pageIndex: 1, pageSize, keyword, projectType: 1 };
        if (status !== '') params.status = status;
        if (jobGroupId !== '') params.jobGroupId = jobGroupId;
        if (filterFrom !== '') params.fromDate = filterFrom;
        if (filterTo !== '') params.toDate = filterTo;
        dispatch(projectPagingAction(params));
        replaceHistory(1, pageSize, keyword);
    };

    const handleResetFilter = () => {
        dispatch(changeValueSearch(''));
        setKeyword('');
        setStatus('');
        setJobGroupId('');
        
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        setFilterFrom(d.toISOString().split('T')[0]);
        setFilterTo(new Date().toISOString().split('T')[0]);
        
        setPageIndex(1);
        
        const fromDateStr = d.toISOString().split('T')[0];
        const toDateStr = new Date().toISOString().split('T')[0];
        
        dispatch(projectPagingAction({ 
            pageIndex: 1, 
            pageSize, 
            keyword: '',
            fromDate: fromDateStr,
            toDate: toDateStr,
            projectType: 1
        }));
        replaceHistory(1, pageSize, '');
    };

    const handleDelete = (id, name) => {
        if (window.confirm(`Bạn có chắc chắn muốn xóa công việc giao "${name}"?`)) {
            dispatch(deleteProjectAction({ id, isAssigned: true })).then((res) => {
                if (res.error) {
                    toast.error('Xóa công việc thất bại');
                } else {
                    toast.success('Xóa công việc thành công');
                    loadData();
                }
            });
        }
    };

    const handleToggleStatus = (item) => {
        const newStatus = item.status === 1 ? 0 : 1;
        const confirmMsg = newStatus === 1
            ? `Bạn có chắc muốn đóng công việc "${item.name}"? Toàn bộ thành viên sẽ được chuyển sang trạng thái "Không hoàn thành".`
            : `Bạn có chắc muốn mở lại công việc "${item.name}"?`;

        if (window.confirm(confirmMsg)) {
            dispatch(changeProjectStatusAction({ id: item.id, status: newStatus, projectType: 1 })).then((res) => {
                if (res.error) {
                    toast.error('Thực hiện thất bại');
                } else {
                    toast.success('Cập nhật trạng thái thành công');
                    loadData();
                }
            });
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('vi-VN');
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        const date = d.toLocaleDateString('vi-VN');
        const time = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        return (
            <div className="d-flex flex-column align-items-center">
                <span>{date}</span>
                <small className="text-muted" style={{ fontSize: '10px', marginTop: '-2px' }}>{time}</small>
            </div>
        );
    };

    const getMemberName = (userId, member = null) => {
        if (!userId) return '-';
        const key = String(userId).trim().toLowerCase();
        const currentUserKey = currentUser?.profile?.sub
            ? String(currentUser.profile.sub).trim().toLowerCase()
            : '';
        if (currentUserKey && key === currentUserKey) {
            const currentFullName = buildFullName(itemAccountAuthInfo);
            if (currentFullName) {
                return currentFullName;
            }
        }
        if (accountLookup.has(key)) {
            return accountLookup.get(key).fullName;
        }
        if (currentUserKey && key === currentUserKey) {
            const currentName = currentUser.profile?.preferred_username || currentUser.profile?.name;
            if (currentName && String(currentName).trim()) {
                return String(currentName).trim();
            }
        }
        if (member) {
            const mName = member.fullName || member.FullName;
            if (mName && mName.trim()) return mName.trim();
            const mUser = member.username || member.Username || member.userName || member.UserName;
            if (mUser && mUser.trim()) return mUser.trim();
        }
        return String(userId).length > 20 ? String(userId).substring(0, 8) + '...' : userId;
    };

    const getTaskStatus = (statusVal) => {
        const num = Number(statusVal);
        return TASK_STATUS_CONST.find(s => s.value === num) || TASK_STATUS_CONST[0];
    };

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header>
                    <h3 className="card-title">Quản lý Giao công việc</h3>
                </Card.Header>
                <Card.Body style={{ minHeight: '600px', paddingBottom: '150px' }}>
                    <div className="d-flex justify-content-end mb-3">
                        <Button variant="info" onClick={() => history.push('/assigned-job/add')}>
                            + Giao công việc mới
                        </Button>
                    </div>

                    <Card className="mb-3 bg-light">
                        <Card.Body className="p-3">
                            <Form onSubmit={handleSearch}>
                                <Row className="align-items-end">
                                    <Col xl={2} lg={2} md={4} sm={6} className="mb-2">
                                        <Form.Label>Từ khoá</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Tên công việc..."
                                            value={keyword}
                                            onChange={(e) => setKeyword(e.target.value)}
                                        />
                                    </Col>
                                    <Col xl={2} lg={2} md={4} sm={6} className="mb-2">
                                        <Form.Label>Trạng thái</Form.Label>
                                        <Select
                                            options={[
                                                { value: '', label: '-- Tất cả --' },
                                                ...PROJECT_STATUS_CONST
                                            ]}
                                            value={
                                                status !== ''
                                                    ? PROJECT_STATUS_CONST.find(opt => opt.value == status) || { value: status, label: status }
                                                    : { value: '', label: '-- Tất cả --' }
                                            }
                                            onChange={(opt) => setStatus(opt && opt.value !== '' ? opt.value : '')}
                                            isSearchable={true}
                                            isClearable={true}
                                            placeholder="-- Tất cả --"
                                        />
                                    </Col>
                                    <Col xl={2} lg={2} md={4} sm={6} className="mb-2">
                                        <Form.Label>Nhóm công việc</Form.Label>
                                        <Select
                                            options={[
                                                { value: '', label: '-- Tất cả --' },
                                                ...jobGroups.map(jg => ({ value: jg.id, label: jg.name }))
                                            ]}
                                            value={
                                                jobGroupId !== ''
                                                    ? jobGroups.map(jg => ({ value: jg.id, label: jg.name })).find(opt => opt.value == jobGroupId) || { value: jobGroupId, label: jobGroupId }
                                                    : { value: '', label: '-- Tất cả --' }
                                            }
                                            onChange={(opt) => setJobGroupId(opt && opt.value !== '' ? opt.value : '')}
                                            isSearchable={true}
                                            isClearable={true}
                                            placeholder="-- Tất cả --"
                                        />
                                    </Col>
                                    <Col xl={2} lg={2} md={4} sm={6} className="mb-2">
                                        <Form.Label>Từ ngày</Form.Label>
                                        <DatePicker 
                                            className="form-control"
                                            selected={filterFrom ? new Date(filterFrom) : null}
                                            onChange={(date) => setFilterFrom(date ? moment(date).format('YYYY-MM-DD') : '')}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="dd/MM/yyyy"
                                            isClearable
                                        />
                                    </Col>
                                    <Col xl={2} lg={2} md={4} sm={6} className="mb-2">
                                        <Form.Label>Đến ngày</Form.Label>
                                        <DatePicker 
                                            className="form-control"
                                            selected={filterTo ? new Date(filterTo) : null}
                                            onChange={(date) => setFilterTo(date ? moment(date).format('YYYY-MM-DD') : '')}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="dd/MM/yyyy"
                                            isClearable
                                        />
                                    </Col>
                                    <Col xl={2} lg={2} md={4} sm={12} className="mb-2 d-flex" style={{ gap: 8 }}>
                                        <Button variant="primary" type="submit" className="flex-grow-1">Tìm kiếm</Button>
                                        <Button variant="secondary" onClick={handleResetFilter} className="flex-grow-1">Xoá lọc</Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>

                    <div className="d-flex align-items-center mb-3">
                        <label className="mr-2 mb-0">Hiển thị:</label>
                        <Form.Control
                            as="select"
                            size="sm"
                            style={{ width: '80px', marginLeft: '8px' }}
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPageIndex(1);
                                replaceHistory(1, Number(e.target.value), keyword);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </Form.Control>
                    </div>

                    <div className="table-responsive" style={{ overflow: 'visible' }}>
                        <Table striped bordered hover style={{ fontSize: '15px', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '5%' }} />
                                <col style={{ width: '18%' }} />
                                <col style={{ width: '18%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '120px' }} />
                            </colgroup>
                            <thead className="bg-light">
                                <tr className="text-center">
                                    <th style={{ fontSize: '16px' }}>STT</th>
                                    <th style={{ fontSize: '16px' }}>Tên công việc</th>
                                    <th style={{ fontSize: '16px' }}>Mô tả</th>
                                    <th style={{ fontSize: '16px' }}>Người thực hiện</th>
                                    <th style={{ fontSize: '16px' }}>Trạng thái</th>
                                    <th style={{ fontSize: '16px' }}>Tiến độ</th>
                                    <th style={{ fontSize: '16px' }}>Thời gian dự kiến</th>
                                    <th style={{ fontSize: '16px' }}>Thực tế hoàn thành</th>
                                    <th style={{ fontSize: '16px' }}>Chức năng</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list && list.length > 0 ? (
                                    list.map((item, index) => {
                                        const itemStatus = item.status !== undefined ? item.status : 0;
                                        const progress = item.progress !== undefined ? item.progress : 0;
                                        const statusLabel = PROJECT_STATUS_CONST.find(o => Number(o.value) === Number(itemStatus));

                                        return (
                                            <tr key={item.id}>
                                                <td className="text-center">{(pageIndex - 1) * pageSize + index + 1}</td>
                                                <td className="font-weight-bold">
                                                    <div>
                                                        <span style={{ cursor: 'pointer', color: '#007bff' }} onClick={() => setDetailProject(normalizeProject(item))}>
                                                            {item.name}
                                                        </span>
                                                    </div>
                                                    <div className="mt-1 text-muted" style={{ fontSize: '12.5px', fontWeight: '500' }}>
                                                        {getMemberName(item.createdBy)}
                                                        {(item.annualJobName || item.jobGroupName) && ` - ${item.annualJobName || item.jobGroupName} (${item.jobGroupWeight})`}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ maxHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {item.description}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '14px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }}>
                                                        {item.members && item.members.length > 0
                                                            ? item.members.map(m => {
                                                                const name = getMemberName(m.userId ?? m.UserId, m);
                                                                const w = m.weight ?? m.Weight;
                                                                return w !== undefined && w !== null ? `${name} (${Number(w).toFixed(2)})` : name;
                                                            }).join(', ')
                                                            : '-'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {(() => {
                                                        const style = getStatusStyle(statusLabel?.color || 'secondary');
                                                        return (
                                                            <span 
                                                                style={{ 
                                                                    fontSize: '0.7rem', 
                                                                    padding: '2px 8px',
                                                                    borderRadius: '12px',
                                                                    fontWeight: '600',
                                                                    backgroundColor: style.bg,
                                                                    color: style.text,
                                                                    border: `1px solid ${style.border}`,
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '4px',
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.3px',
                                                                    maxWidth: '100%',
                                                                    whiteSpace: 'normal',
                                                                    lineHeight: '1.2',
                                                                    textAlign: 'center',
                                                                    wordBreak: 'break-word'
                                                                }}
                                                            >
                                                                <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: style.text, flexShrink: 0 }}></span>
                                                                {statusLabel?.label || 'Đang thực hiện'}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="text-center">
                                                    <div>
                                                        <div className="progress" style={{ height: 8, marginBottom: 2 }}>
                                                            <div className="progress-bar bg-success" style={{ width: `${progress}%` }} />
                                                        </div>
                                                        <small>{progress}%</small>
                                                    </div>
                                                </td>
                                                <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                                    {item.startDate || item.endDate ? (
                                                        <small>{formatDate(item.startDate)} – {formatDate(item.endDate)}</small>
                                                    ) : '-'}
                                                </td>
                                                <td className="text-center font-weight-bold text-success" style={{ verticalAlign: 'middle' }}>
                                                    {item.actualEndDate ? formatDateTime(item.actualEndDate) : '-'}
                                                </td>
                                                <td className="text-center" style={{ verticalAlign: 'middle', width: '120px' }}>
                                                    <Dropdown align="end" style={{ display: 'inline-block' }}>
                                                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                                                            Thao tác
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                                            <Dropdown.Item onClick={() => setDetailProject(normalizeProject(item))}>
                                                                Tiến độ
                                                            </Dropdown.Item>
                                                            
                                                            {(isAdmin || item.createdBy === currentUsername) && (
                                                                <>
                                                                    <Dropdown.Item as={NavLink} to={`/assigned-job/edit/${item.id}`}>
                                                                        Sửa
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item onClick={() => handleToggleStatus(item)}>
                                                                        {item.status === 1 ? 'Mở lại công việc' : 'Đóng Project'}
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Divider />
                                                                    <Dropdown.Item className="text-danger" onClick={() => handleDelete(item.id, item.name)}>
                                                                        Xóa
                                                                    </Dropdown.Item>
                                                                </>
                                                            )}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4 text-muted">Không có dữ liệu</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {total > pageSize && (
                        <div className="d-flex justify-content-end mt-3">
                            <Pagination
                                activePage={pageIndex}
                                itemsCountPerPage={pageSize}
                                totalItemsCount={total}
                                pageRangeDisplayed={5}
                                onChange={(page) => setPageIndex(page)}
                                itemClass="page-item"
                                linkClass="page-link"
                             />
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Modal
                show={!!detailProject}
                onHide={() => setDetailProject(null)}
                size="md"
                centered
                style={{ zIndex: 1060 }}
                backdropClassName="custom-modal-backdrop"
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                        Thành viên: {detailProject?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailProject && (
                        <>
                            {detailProject.description && (
                                <p className="text-muted mb-3" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                                    📋 {detailProject.description}
                                </p>
                            )}
                            <div className="mb-2 text-muted" style={{ fontSize: 12 }}>
                                <strong>Thời gian:</strong> {formatDate(detailProject.startDate)} – {formatDate(detailProject.endDate)}
                            </div>
                            <h6 className="mt-3 mb-2">Trạng thái các thành viên:</h6>
                            {detailProject.members && detailProject.members.length > 0 ? (
                                <Table bordered size="sm" className="mb-0" style={{ fontSize: 13 }}>
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-2">Tên / Account</th>
                                            <th className="text-center" style={{ width: 90 }}>Trọng số</th>
                                            <th className="text-center" style={{ width: 130 }}>Trạng thái</th>
                                            <th className="text-center" style={{ width: 140 }}>Hoàn thành lúc</th>
                                            <th className="text-center" style={{ width: 180 }}>Minh chứng</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailProject.members.map(m => {
                                            const uid = m.userId ?? m.UserId;
                                            const s = getTaskStatus(m.status ?? m.Status);
                                            const fullName = getMemberName(uid, m);
                                            const key = String(uid).trim().toLowerCase();
                                            const username = accountLookup.get(key)?.username || m.username || m.Username || m.userName || m.UserName || (String(uid).length > 20 ? String(uid).substring(0, 8) : uid);
                                            return (
                                                <tr key={uid}>
                                                    <td className="ps-2">
                                                        <div className="font-weight-bold">{fullName || <span className="text-muted fst-italic">(Chưa có tên)</span>}</div>
                                                        <small className="text-muted">{username}</small>
                                                    </td>
                                                    <td className="text-center align-middle fw-bold">
                                                        {(() => {
                                                            const w = m.memberWeight ?? m.MemberWeight ?? m.weight ?? m.Weight;
                                                            return w !== undefined && w !== null ? Number(w) : '-';
                                                        })()}
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        {(() => {
                                                            const style = getStatusStyle(s.color || 'secondary');
                                                            return (
                                                                <span 
                                                                    style={{ 
                                                                        fontSize: '0.65rem', 
                                                                        padding: '2px 8px',
                                                                        borderRadius: '10px',
                                                                        fontWeight: '600',
                                                                        backgroundColor: style.bg,
                                                                        color: style.text,
                                                                        border: `1px solid ${style.border}`,
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '4px',
                                                                        textTransform: 'uppercase',
                                                                        maxWidth: '100%',
                                                                        whiteSpace: 'normal',
                                                                        lineHeight: '1.2',
                                                                        textAlign: 'center',
                                                                        wordBreak: 'break-word'
                                                                    }}
                                                                >
                                                                    <span style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: style.text, flexShrink: 0 }}></span>
                                                                    {s.label}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        {m.actualEndDate ? formatDateTime(m.actualEndDate) : '-'}
                                                    </td>
                                                    <td className="align-middle">
                                                        {m.evidenceFiles && m.evidenceFiles.length > 0 ? (
                                                            <ul className="mb-0 ps-3" style={{ fontSize: '0.85rem' }}>
                                                                {m.evidenceFiles.map(file => (
                                                                    <li key={file.id}>
                                                                        <a href={`${BaseGatewayImage}${file.filePath}`} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">
                                                                            <i className="ti-clip me-1"></i>{file.fileName}
                                                                        </a>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <span className="text-muted fst-italic" style={{ fontSize: '0.85rem' }}>Không có</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted">Chưa có thành viên nào được giao.</p>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" size="sm" as={NavLink} to={`/assigned-job/edit/${detailProject?.id}`}>
                        Sửa
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setDetailProject(null)}>Đóng</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AssignedJobList;
